import type {
  FloorLevelCode,
  ISODateString,
  PropertyAddress,
  UnitPossessionTimeline,
} from '@/lib/types/property';

const numberFormatter = new Intl.NumberFormat('en-CA', {
  maximumFractionDigits: 0,
});

const currencyFormatter = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const availabilityDateFormatter = new Intl.DateTimeFormat('en-CA', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

export function humanizeAddressText(value: string | null | undefined): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
}

export function formatPropertyAddress(address: PropertyAddress): string {
  const street = [
    humanizeAddressText(address.streetNumber),
    humanizeAddressText(address.streetName),
    humanizeAddressText(address.streetType),
  ]
    .filter(Boolean)
    .join(' ');
  const cityLine = [humanizeAddressText(address.city), humanizeAddressText(address.province)]
    .filter(Boolean)
    .join(', ');

  return [street, cityLine].filter(Boolean).join(' • ');
}

export function formatUnitLabel(value: {
  unitLabel?: string | null;
  unitCode?: string | null;
}): string {
  const unitLabel = typeof value.unitLabel === 'string' ? value.unitLabel.trim() : '';
  if (unitLabel.length > 0) {
    return unitLabel;
  }

  const unitCode = typeof value.unitCode === 'string' ? value.unitCode.trim() : '';
  return unitCode;
}

export function formatFloorLevelLabel(
  floorLevel: FloorLevelCode | null | undefined,
  options?: { emptyLabel?: string; prefix?: string },
): string {
  const trimmed = typeof floorLevel === 'string' ? floorLevel.trim() : '';
  if (trimmed.length === 0) {
    return options?.emptyLabel ?? 'Floor not set';
  }

  const prefix = options?.prefix;
  return typeof prefix === 'string' && prefix.length > 0 ? `${prefix} ${trimmed}` : trimmed;
}

export function formatSquareFeet(value: number | null): string {
  if (value == null) {
    return '';
  }

  return numberFormatter.format(value);
}

export function formatRentRange(min: number | null, max: number | null): string {
  if (min === null && max === null) {
    return '';
  }

  if (min !== null && max !== null) {
    if (min === max) {
      return `${currencyFormatter.format(min)}/SF`;
    }
    return `${currencyFormatter.format(min)}–${currencyFormatter.format(max)}/SF`;
  }

  const value = min ?? max ?? 0;
  const prefix = min !== null ? 'From ' : 'Up to ';
  return `${prefix}${currencyFormatter.format(value)}/SF`;
}

export function formatAvailabilityRange(
  start: ISODateString | null,
  end: ISODateString | null,
): string {
  const hasStart = typeof start === 'string' && start.length > 0;
  const hasEnd = typeof end === 'string' && end.length > 0;

  const startValue = hasStart ? start : null;
  const endValue = hasEnd ? end : null;

  if (startValue === null && endValue === null) {
    return '';
  }

  const intl = new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  if (startValue !== null && endValue !== null) {
    return `${intl.format(new Date(startValue))} – ${intl.format(new Date(endValue))}`;
  }

  if (startValue !== null) {
    return `From ${intl.format(new Date(startValue))}`;
  }

  if (endValue !== null) {
    return `Until ${intl.format(new Date(endValue))}`;
  }

  return '';
}

export function formatAvailabilityDateLabel(
  value: ISODateString | null | undefined,
  options?: { nowLabel?: string; prefix?: string },
): string {
  if (typeof value !== 'string' || value.length === 0) {
    return '';
  }

  const parsed = parseDateOnly(value);
  if (!parsed) {
    return '';
  }

  const dateOnly = toUtcDateOnly(parsed);
  const today = toUtcDateOnly(new Date());
  if (dateOnly <= today) {
    return options?.nowLabel ?? 'Available now';
  }

  const prefix = options?.prefix ?? 'Available';
  return `${prefix} ${availabilityDateFormatter.format(dateOnly)}`;
}

export function formatMoveInTimingLabel(
  availabilityStart: ISODateString | null | undefined,
  possessionTimeline: UnitPossessionTimeline | null | undefined,
  options?: { immediateLabel?: string; negotiableLabel?: string; datePrefix?: string },
): string {
  const immediateLabel = resolveLabel(options?.immediateLabel, 'Immediate');
  const negotiableLabel = resolveLabel(options?.negotiableLabel, 'Negotiable');
  const datePrefix = resolveLabel(options?.datePrefix, 'From');
  const dateLabel = formatAvailabilityDateLabel(availabilityStart, {
    nowLabel: immediateLabel,
    prefix: datePrefix,
  });

  if (dateLabel.length > 0) {
    return dateLabel;
  }

  if (possessionTimeline === 'negotiable') {
    return negotiableLabel;
  }

  return immediateLabel;
}

export function isAvailabilityDateInFuture(value: ISODateString | null | undefined): boolean {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }
  const parsed = parseDateOnly(value);
  if (!parsed) {
    return false;
  }
  return toUtcDateOnly(parsed) > toUtcDateOnly(new Date());
}

function parseDateOnly(value: string): Date | null {
  const normalized = value.length === 10 ? `${value}T00:00:00Z` : value;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

function toUtcDateOnly(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function resolveLabel(value: string | null | undefined, fallback: string): string {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : fallback;
}
